import clsx from "clsx";
import axios from "axios";
import { debounce } from "lodash";
import { BiRupee } from "react-icons/bi";
import { createPortal } from "react-dom";
import { useDebounce } from "@uidotdev/usehooks";
import { useRouter } from "@/app/hooks/useRouter";
import React, { useEffect, useState } from "react";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { Search, X, Info, Clock } from "lucide-react";
import { CART_MODULE } from "@/api_framework/api_config";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";

interface IUnifiedLabServiceSearchComponentProps {
  containerClassName?: string;
  placeholder?: string;
  clinicId?: string;
  portalId?: string;
  // Optional surge context: price the results for this appointment type + slot.
  serviceTypeId?: string;
  slotStart?: string;
}

const STYLES = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 5px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 8px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  .price-row {
    transition: all 0.15s ease;
    border-left: 3px solid transparent;
  }
  .price-row:hover {
    border-left-color: #3b82f6;
    background: linear-gradient(90deg, #eff6ff 0%, #ffffff 100%);
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .price-row-animate {
    animation: fadeInUp 0.2s ease forwards;
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .skeleton-shimmer {
    background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
  }
`;

const SKELETON_ROWS = [1, 2, 3, 4, 5, 6, 7];

const TableHeader = () => (
  <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200/80 flex-shrink-0">
    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Service / Lab Test</span>
    <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Price</span>
  </div>
);

export const UnifiedLabServiceSearchComponent: React.FC<IUnifiedLabServiceSearchComponentProps> = ({
  containerClassName,
  placeholder = "Search Services & Labs...",
  clinicId,
  portalId,
  serviceTypeId,
  slotStart,
}) => {
  const { getRouteKey } = useRouter();
  const configuration = useConfiguration();
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query?.trim(), 300);
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);
  const customerProfileState = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"))?.routeState?.state as
    | ICustomerProfileState
    | undefined;
  const petId = customerProfileState?.petId;
  const customerId = customerProfileState?.customerId;
  const { execute: logClick } = useClickLogAPI();
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);

  const debounceSearch = debounce(async () => {
    if (!debouncedQuery || debouncedQuery.length < 3 || !clinicId) return;
    setIsLoading(true);
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const url = CART_MODULE?.CART_SEARCH?.baseURL;
      const params = new URLSearchParams();
      params.append("pet_id", petId ?? "");
      params.append("clinic_id", clinicId);
      params.append("search_keyword", debouncedQuery);
      params.append("search_tag", "Services");
      params.append("search_tag", "Lab");
      if (serviceTypeId) {
        params.append("service_type_id", serviceTypeId);
        if (slotStart) params.append("slot_start", slotStart);
      }
      const res = await axios.get(url, {
        params,
        headers: {
          Authorization: accessToken ?? "",
          "Content-Type": "application/json",
        },
      });
      const list = res.data?.status && Array.isArray(res.data.data) ? res.data.data : [];
      setSearchResults(list);
      logClick({
        event_type: "PRICE_SEARCH_IN_POPUP_CLICK",
        scr_id: "CHECK_PRICE_MODAL",
        client_id: customerId,
        pet_id: petId,
        vms_user_id: vmsUserId,
        clinic_id: clinicId,
        search_item: debouncedQuery,
        search_result: list?.length ?? 0,
        timestamp: Date.now(),
      });
    } catch (e) {
      console.error(e);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, 500);

  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      debounceSearch();
    } else {
      setSearchResults([]);
    }
    return () => {
      debounceSearch.cancel();
    };
  }, [debouncedQuery, clinicId, serviceTypeId, slotStart]);

  useEffect(() => {
    if (portalId) {
      const el = document.getElementById(portalId);
      if (el) {
        setPortalElement(el);
      } else {
        const timer = setTimeout(() => {
          setPortalElement(document.getElementById(portalId));
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [portalId]);

  const renderEmptyState = (icon: React.ReactNode, title: string, description: string, gradientFrom: string, gradientTo: string) => (
    <div className="flex flex-col items-center justify-center py-12 text-center flex-1">
      <div className={`p-3.5 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-2xl mb-4 shadow-sm`}>
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      <p className="text-xs text-gray-400 mt-1.5 max-w-sm leading-relaxed">{description}</p>
    </div>
  );

  const resultsContent = (
    <div className="flex flex-col w-full h-full min-h-[220px]">
      <style>{STYLES}</style>
      <div className="bg-white flex-1 flex flex-col justify-start min-h-0">
        {!clinicId ? (
          renderEmptyState(
            <Info className="w-6 h-6 text-amber-500 animate-pulse" />,
            "Clinic Not Selected",
            "Please select a city and clinic from the filters above to search prices.",
            "from-amber-50",
            "to-orange-50"
          )
        ) : query.trim().length < 3 ? (
          renderEmptyState(
            <Search className="w-6 h-6 text-[#0069f9] animate-pulse" />,
            "Search Prices",
            "Type at least 3 characters to search for clinic services or lab tests.",
            "from-blue-50",
            "to-indigo-50"
          )
        ) : isLoading ? (
          <div className="flex flex-col flex-1 min-h-0 rounded-lg overflow-hidden border border-gray-200/80 shadow-sm">
            <TableHeader />
            <div className="flex flex-col flex-1 overflow-y-auto custom-scrollbar min-h-0 bg-white">
              {SKELETON_ROWS.map((i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100/80 last:border-b-0">
                  <div className="h-3.5 skeleton-shimmer rounded-md" style={{ width: `${25 + (i * 11) % 40}%` }} />
                  <div className="h-3.5 w-14 skeleton-shimmer rounded-md" />
                </div>
              ))}
            </div>
          </div>
        ) : searchResults.length === 0 ? (
          renderEmptyState(
            <Search className="w-6 h-6 text-gray-400" />,
            "No Results Found",
            `We couldn't find any services or lab tests matching "${debouncedQuery}".`,
            "from-gray-50",
            "to-slate-100"
          )
        ) : (
          <div className="flex flex-col flex-1 min-h-0 rounded-lg overflow-hidden border border-gray-200/80 shadow-sm">
            <TableHeader />
            <div className="flex flex-col flex-1 overflow-y-auto custom-scrollbar min-h-0 bg-white">
              {searchResults.map((result, index) => {
                const cleanedName = result.name?.split("_").join(" ");
                return (
                  <div
                    key={result.id}
                    className="price-row price-row-animate flex items-start justify-between gap-4 px-4 py-2.5 border-b border-gray-100/80 last:border-b-0 cursor-default"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    <div className="min-w-0 max-w-[75%]">
                      <span className="text-[12px] font-medium text-gray-700 leading-relaxed block">
                        {cleanedName}
                      </span>
                      {result.tat && (
                        <div className="mt-1">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[9px] font-semibold whitespace-nowrap">
                            <Clock className="w-2.5 h-2.5 text-gray-400" />
                            TAT: {result.tat} {Number(result.tat) === 1 ? "Hr" : "Hrs"}
                          </span>
                        </div>
                      )}
                    </div>
                    <span className="flex items-center text-gray-900 font-semibold text-[13px] flex-shrink-0 tabular-nums mt-0.5">
                      <BiRupee className="text-sm text-gray-500" />
                      {result.price}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={clsx("flex flex-col w-full", containerClassName)}>
      <div className="relative flex items-center bg-white border border-gray-200 rounded-lg transition-all duration-200 px-3 py-[9.5px] w-full shadow-sm hover:shadow focus-within:shadow focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100">
        <Search className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="border-none focus:ring-0 p-0 pr-6 text-gray-900 text-[10px] sm:text-xs font-medium w-full outline-none placeholder-gray-400 bg-transparent"
        />
        {query.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setSearchResults([]);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-red-50 transition-colors flex items-center justify-center group"
          >
            <X className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-500 transition-colors" />
          </button>
        )}
      </div>
      {portalElement ? createPortal(resultsContent, portalElement) : resultsContent}
    </div>
  );
};

export default UnifiedLabServiceSearchComponent;
